import { useState } from 'react';
import { AlertCircle, RefreshCw, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useReplayWebhook } from '../hooks/useReplayWebhook';

interface ReplayWebhookModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultEventId?: string;
}

export function ReplayWebhookModal({
  isOpen,
  onClose,
  defaultEventId = '',
}: ReplayWebhookModalProps) {
  const [eventId, setEventId] = useState(defaultEventId);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const replayMutation = useReplayWebhook();

  if (!isOpen) return null;

  const handleReplay = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!eventId.trim()) {
      setError('Webhook Event ID is required');
      return;
    }

    try {
      await replayMutation.mutateAsync(eventId.trim());
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 1500);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to replay webhook event');
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          data-testid="replay-webhook-modal"
          className="w-full max-w-md bg-surface-container-lowest border border-border-subtle rounded-3xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 border-b border-border-subtle flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold text-on-surface">Replay Dead-Letter Webhook</h2>
              <p className="text-xs text-on-surface-variant mt-0.5">
                Resets a failed DLQ webhook event back to PENDING status.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-on-surface-variant hover:text-on-surface hover:bg-white/5 rounded-xl transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleReplay} className="p-6 space-y-4">
            {error && (
              <div className="p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/30 flex items-start gap-2.5 text-rose-300 text-xs">
                <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                <p className="flex-1 font-medium">{error}</p>
              </div>
            )}

            {success && (
              <div
                data-testid="toast-success"
                className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold"
              >
                Event successfully reset to PENDING and re-enqueued on BullMQ!
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-on-surface-variant block">
                Webhook Event ID (UUID)
              </label>
              <input
                type="text"
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                placeholder="evt_0192a8b3-c4d5-7e8f-9a0b-1c2d3e4f5a6b"
                className="w-full px-3.5 py-2.5 bg-surface-container/60 border border-border-subtle rounded-xl text-xs font-mono text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary transition-all"
              />
            </div>

            <p className="text-[11px] text-on-surface-variant leading-relaxed">
              ℹ️ Replaying will push the webhook back onto the processor queue. Ensure transient downstream errors (e.g. database locks) have subsided.
            </p>

            <div className="pt-3 border-t border-border-subtle flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={replayMutation.isPending}
                className="px-4 py-2 rounded-xl border border-border-subtle text-xs text-on-surface-variant hover:text-on-surface hover:bg-white/5 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={replayMutation.isPending}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-primary hover:bg-primary-hover text-on-primary font-bold text-xs shadow-md transition-all disabled:opacity-50"
              >
                {replayMutation.isPending ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <span>Replay Event</span>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
